import React, { useState, useEffect } from "react";
import { Button, Form, Alert } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { toast } from "react-toastify";
import DepositFormFields from "./transaction-forms/DepositFormFields";
import WithdrawFormFields from "./transaction-forms/WithdrawFormFields";
import TransferFormFields from "./transaction-forms/TransferFormFields";
import ConfirmModal, { MODAL_SIZES } from "./ConfirmModal";
import callApi from "../helpers/CallApi";
import {
  depositAmount,
  getBankAccountList,
  transferAmount,
  withdrawAmount,
} from "../helpers/API";

const TRANSACTION_TYPES = {
  DEPOSIT: "deposit",
  WITHDRAW: "withdraw",
  TRANSFER: "transfer",
};

const TransactionModal = ({
  show,
  toggle,
  transactionType,
  selectedAccount = null,
  onTransactionComplete,
}) => {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formDataToSave, setFormDataToSave] = useState(null);
  const [selectedAccountBalance, setSelectedAccountBalance] = useState(0);
  const [receiverAccountBalance, setReceiverAccountBalance] = useState(0);

  // Dynamic initial values based on transaction type
  const getInitialValues = () => {
    const baseValues = {
      accountId: selectedAccount?.id || "",
      accountType: selectedAccount?.accountType,
      amount: "",
      remarks: "",
    };

    if (transactionType === TRANSACTION_TYPES.TRANSFER) {
      return {
        ...baseValues,
        fromBankAccount: selectedAccount?.id || "",
        toBankAccount: "",
        transferAmount: "",
      };
    }

    return baseValues;
  };

  // Dynamic validation schema
  const getValidationSchema = () => {
    const baseSchema = {
      amount: Yup.number()
        .min(0.01, t("transaction.validation.amountMin"))
        .max(999999999.99, t("transaction.validation.amountMax"))
        .required(t("transaction.validation.amountRequired")),
      remarks: Yup.string().max(500, t("transaction.validation.remarksMax")),
    };

    if (transactionType === TRANSACTION_TYPES.DEPOSIT) {
      return Yup.object({
        ...baseSchema,
        accountType: Yup.string().required(
          t("transaction.validation.accountTypeRequired")
        ),
        accountId: Yup.string().when("accountType", {
          is: (accountType) => accountType !== "cash_account",
          then: (schema) =>
            schema.required(t("transaction.validation.bankAccountRequired")),
          otherwise: (schema) => schema.notRequired(),
        }),
      });
    }

    if (transactionType === TRANSACTION_TYPES.WITHDRAW) {
      return Yup.object({
        ...baseSchema,
        accountId: Yup.string().required(
          t("transaction.validation.bankAccountRequired")
        ),
        amount: baseSchema.amount.test(
          "sufficient-balance",
          t("transaction.validation.insufficientBalance"),
          function (value) {
            return value <= selectedAccountBalance;
          }
        ),
      });
    }

    if (transactionType === TRANSACTION_TYPES.TRANSFER) {
      return Yup.object({
        fromBankAccount: Yup.string().required(
          t("transaction.validation.fromAccountRequired")
        ),
        toBankAccount: Yup.string()
          .required(t("transaction.validation.toAccountRequired"))
          .test(
            "different-accounts",
            t("transaction.validation.sameAccountError"),
            function (value) {
              return value !== this.parent.fromBankAccount;
            }
          ),
        transferAmount: Yup.number()
          .min(0.01, t("transaction.validation.amountMin"))
          .max(999999999.99, t("transaction.validation.amountMax"))
          .test(
            "sufficient-balance",
            t("transaction.validation.insufficientBalance"),
            function (value) {
              return value <= selectedAccountBalance;
            }
          )
          .required(t("transaction.validation.amountRequired")),
        remarks: Yup.string().max(500, t("transaction.validation.remarksMax")),
      });
    }

    return Yup.object(baseSchema);
  };

  // Load accounts on component mount
  useEffect(() => {
    if (show) {
      loadAccounts();
    }
  }, [show]);

  // Update selected account balance when account changes
  useEffect(() => {
    if (selectedAccount) {
      setSelectedAccountBalance(selectedAccount.balance || 0);
    }
  }, [selectedAccount, show]);

  const loadAccounts = async () => {
    try {
      callApi({
        callback: getBankAccountList,
        payload: { pagination: false, includeCashAccount: true },
        onSuccess: ({ data }) => {
          setAccounts(
            data?.filter(
              (acc) => acc.isActive && acc.id !== selectedAccount?.id
            ) || []
          );
        },
        onError: (message) => {
          toast.error(message || t("transaction.messages.loadAccountsError"));
        },
      });
    } catch (error) {
      console.error("Error loading accounts:", error);
    }
  };

  const handleAccountChange = (
    accountId,
    setFieldValue,
    fieldName = "bankAccount"
  ) => {
    const account = accounts.find((acc) => acc.id === parseInt(accountId));
    if (account) {
      setFieldValue(fieldName, accountId);
      if (fieldName === "bankAccount" || fieldName === "fromBankAccount") {
        setSelectedAccountBalance(Number(account?.balance || 0));
      } else if (fieldName === "toBankAccount") {
        setReceiverAccountBalance(Number(account?.balance || 0));
      }
    }
  };

  // This is the actual form submit handler
  const handleFormSubmit = (values, { setSubmitting }) => {
    setFormDataToSave(values);
    setShowConfirmation(true);
    setSubmitting(false);
  };

  const handleConfirmedSave = () => {
    const apiCallbacks = {
      [TRANSACTION_TYPES.DEPOSIT]: depositAmount,
      [TRANSACTION_TYPES.WITHDRAW]: withdrawAmount,
      [TRANSACTION_TYPES.TRANSFER]: transferAmount,
    };

    const callback = apiCallbacks[transactionType];
    if (!callback) {
      toast.success(t("transaction.messages.apiError"));
      handleClose();
      return;
    }

    // Transform data based on transaction type
    const transformedData = transformFormData(formDataToSave);

    callApi({
      callback,
      payload: transformedData,
      onSuccess: ({ message, data }) => {
        toast.success(message || t("transaction.messages.success"));
        handleClose();
        onTransactionComplete && onTransactionComplete(data);
      },
      onError: (message) => {
        toast.error(message || t("transaction.messages.error"));
      },
    });
  };

  const transformFormData = (data) => {
    if (transactionType === TRANSACTION_TYPES.TRANSFER) {
      return {
        fromAccountId: parseInt(data.fromBankAccount),
        toAccountId: parseInt(data.toBankAccount),
        amount: parseFloat(data.transferAmount),
        description: data.remarks || t("transaction.message.transferRemark"),
      };
    }

    return {
      accountId: parseInt(data.accountId),
      amount: parseFloat(data.amount),
      accountType: data.accountType,
      description:
        data.remarks ||
        (transactionType === TRANSACTION_TYPES.DEPOSIT
          ? t("transaction.message.deposit")
          : t("transaction.message.withdraw")),
    };
  };

  const handleClose = () => {
    setShowConfirmation(false);
    setFormDataToSave(null);
    setSelectedAccountBalance(0);
    setReceiverAccountBalance(0);
    toggle();
  };

  const getModalTitle = () => {
    const titles = {
      [TRANSACTION_TYPES.DEPOSIT]: t("transaction.deposit.title"),
      [TRANSACTION_TYPES.WITHDRAW]: t("transaction.withdraw.title"),
      [TRANSACTION_TYPES.TRANSFER]: t("transaction.transfer.title"),
    };
    return titles[transactionType] || t("transaction.common.title");
  };

  const getConfirmationMessage = () => {
    const messages = {
      [TRANSACTION_TYPES.DEPOSIT]: t("transaction.deposit.confirmationMessage"),
      [TRANSACTION_TYPES.WITHDRAW]: t(
        "transaction.withdraw.confirmationMessage"
      ),
      [TRANSACTION_TYPES.TRANSFER]: t(
        "transaction.transfer.confirmationMessage"
      ),
    };
    return (
      messages[transactionType] || t("transaction.common.confirmationMessage")
    );
  };

  return (
    <>
      <Modal isOpen={show} toggle={handleClose} size="lg" centered>
        <Formik
          initialValues={getInitialValues()}
          validationSchema={getValidationSchema()}
          onSubmit={handleFormSubmit} // Use the actual form submit handler
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit, // This is Formik's handleSubmit
            isSubmitting,
            setFieldValue,
          }) => (
            <>
              <ModalHeader toggle={handleClose}>{getModalTitle()}</ModalHeader>
              <ModalBody>
                {/* Remove the Form wrapper and use handleSubmit directly */}
                <form onSubmit={handleSubmit} noValidate>
                  {/* Deposit Form Fields */}
                  {transactionType === TRANSACTION_TYPES.DEPOSIT && (
                    <DepositFormFields
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      accounts={accounts}
                      selectedAccountBalance={Number(
                        selectedAccountBalance || 0
                      )}
                      handleAccountChange={handleAccountChange}
                      t={t}
                    />
                  )}

                  {/* Withdraw Form Fields */}
                  {transactionType === TRANSACTION_TYPES.WITHDRAW && (
                    <WithdrawFormFields
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      accounts={accounts}
                      selectedAccountBalance={Number(
                        selectedAccountBalance || 0
                      )}
                      handleAccountChange={handleAccountChange}
                      t={t}
                    />
                  )}

                  {/* Transfer Form Fields */}
                  {transactionType === TRANSACTION_TYPES.TRANSFER && (
                    <TransferFormFields
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      accounts={accounts}
                      selectedAccountBalance={Number(
                        selectedAccountBalance || 0
                      )}
                      receiverAccountBalance={receiverAccountBalance}
                      handleAccountChange={handleAccountChange}
                      t={t}
                    />
                  )}
                </form>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" onClick={handleClose}>
                  {t("transaction.common.cancel")}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  onClick={handleSubmit} // Use Formik's handleSubmit
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t("transaction.common.processing")
                    : transactionType === TRANSACTION_TYPES.TRANSFER
                      ? t("transaction.transfer.transferButton")
                      : t("transaction.common.save")}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </Modal>

      <ConfirmModal
        isOpen={showConfirmation}
        toggle={() => setShowConfirmation(false)}
        onConfirm={handleConfirmedSave}
        variant={"SAVE"}
        title={t("transaction.confirmation.title")}
        message={getConfirmationMessage()}
        size={MODAL_SIZES.MEDIUM}
      >
        <div>
          <Alert color="warning" className="mb-3">
            <i className="ri-alert-line me-2"></i>
            {getConfirmationMessage()}
          </Alert>
          {formDataToSave && (
            <div className="transaction-summary">
              <h6>{t("transaction.confirmation.summary")}</h6>
              <ul className="list-unstyled">
                <li>
                  <strong>{t("transaction.common.amount")}:</strong> ₹
                  {formDataToSave.amount || formDataToSave.transferAmount}
                </li>
                <li>
                  <strong>{t("transaction.common.type")}:</strong>{" "}
                  {transactionType.toUpperCase()}
                </li>
                {formDataToSave.remarks && (
                  <li>
                    <strong>{t("transaction.common.remarks")}:</strong>{" "}
                    {formDataToSave.remarks}
                  </li>
                )}
              </ul>
            </div>
          )}
          <p className="mb-0 text-muted">
            {t("transaction.confirmation.note")}
          </p>
        </div>
      </ConfirmModal>
    </>
  );
};

export default TransactionModal;
export { TRANSACTION_TYPES };
